import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag, ArrowRight, Instagram, Facebook } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { CATEGORIES, BUSINESS } from "@/data/content";

export default function Header() {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();

  const go = (path) => {
    setMenu(false);
    navigate(path);
  };

  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = menu ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menu]);

  return (
    <>
    <header className="sticky top-0 z-40 border-b border-wood-900/10 bg-cream/90 backdrop-blur-md">
      <div className="mx-auto grid max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-3 md:flex md:justify-between md:px-8 md:py-4">
        {/* Mobile hamburger — clear, high-contrast circular button */}
        <button
          className="flex h-11 w-11 items-center justify-center rounded-full border border-wood-900/15 bg-white text-wood-900 shadow-sm transition-colors hover:bg-honey/15 md:hidden"
          onClick={() => setMenu(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>

        <Link
          to="/"
          data-testid="logo-link"
          className="flex flex-col items-center leading-none md:items-start"
        >
          <span className="whitespace-nowrap font-serif text-lg tracking-tight text-wood-900 sm:text-xl md:text-2xl">Washington Family</span>
          <span className="text-[9px] font-medium uppercase tracking-[0.35em] text-honey-dark sm:text-[10px]">Provisions</span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {CATEGORIES.slice(0, 5).map((c) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              data-testid={`nav-${c.slug}`}
              className="text-base font-medium text-wood-700 transition-colors hover:text-honey-dark"
            >
              {c.label}
            </Link>
          ))}
          <Link to="/loose-herbs-botanicals" data-testid="nav-loose-herbs-botanicals" className="text-base font-medium text-wood-700 transition-colors hover:text-honey-dark">
            Loose Herbs
          </Link>
          <Link to="/quiz" data-testid="nav-quiz" className="text-base font-medium text-honey-dark transition-colors hover:text-wood-900">
            Self-Care Quiz
          </Link>
          <Link to="/our-story" data-testid="nav-story" className="text-base font-medium text-wood-700 transition-colors hover:text-honey-dark">
            Our Story
          </Link>
        </nav>

        <button
          onClick={() => setOpen(true)}
          data-testid="cart-button"
          className="relative flex h-11 w-11 items-center justify-center rounded-full text-wood-900 transition-colors hover:bg-honey/15 md:h-auto md:w-auto"
          aria-label="Open cart"
        >
          <ShoppingBag size={22} />
          {count > 0 && (
            <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-honey-dark text-[11px] font-semibold text-cream md:-right-2 md:-top-2">
              {count}
            </span>
          )}
        </button>
      </div>

      </header>

      <AnimatePresence>
        {menu && (
          <motion.div
            className="fixed inset-0 z-50 bg-wood-900/50 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMenu(false)}
          >
            <motion.div
              className="absolute left-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-cream"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", ease: [0.16, 1, 0.3, 1], duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Fixed drawer header */}
              <div className="flex flex-none items-center justify-between border-b border-wood-900/10 px-6 py-5">
                <div className="flex flex-col leading-none">
                  <span className="font-serif text-xl text-wood-900">Washington Family</span>
                  <span className="text-[10px] font-medium uppercase tracking-[0.35em] text-honey-dark">Provisions</span>
                </div>
                <button
                  onClick={() => setMenu(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-wood-900/15 text-wood-900"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable nav list */}
              <nav data-lenis-prevent className="flex-1 overflow-y-auto px-6 py-4">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => go(`/shop/${c.slug}`)}
                    data-testid={`mobile-nav-${c.slug}`}
                    className="flex w-full items-center justify-between border-b border-wood-900/10 py-4 text-left"
                  >
                    <span className="flex flex-col">
                      <span className="font-serif text-2xl leading-tight text-wood-900">{c.label}</span>
                      <span className="text-xs text-wood-500">{c.tagline}</span>
                    </span>
                    <ArrowRight size={18} className="flex-none text-honey-dark" />
                  </button>
                ))}
                <button onClick={() => go("/loose-herbs-botanicals")} data-testid="mobile-nav-loose-herbs-botanicals" className="flex w-full items-center justify-between border-b border-wood-900/10 py-4 text-left">
                  <span className="flex flex-col">
                    <span className="font-serif text-2xl leading-tight text-wood-900">Loose Herbs &amp; Botanicals</span>
                    <span className="text-xs text-wood-500">Herbs, flowers, roots & berries by the gram</span>
                  </span>
                  <ArrowRight size={18} className="flex-none text-honey-dark" />
                </button>
                <button onClick={() => go("/our-story")} data-testid="mobile-nav-story" className="flex w-full items-center justify-between border-b border-wood-900/10 py-4 text-left font-serif text-2xl text-wood-900">
                  Our Story <ArrowRight size={18} className="flex-none text-honey-dark" />
                </button>
                <button onClick={() => go("/contact")} data-testid="mobile-nav-contact" className="flex w-full items-center justify-between py-4 text-left font-serif text-2xl text-wood-900">
                  Contact <ArrowRight size={18} className="flex-none text-honey-dark" />
                </button>
              </nav>

              {/* Fixed footer CTA */}
              <div className="flex-none border-t border-wood-900/10 p-6">
                <button
                  onClick={() => go("/quiz")}
                  data-testid="mobile-nav-quiz"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-wood-900 py-4 text-base font-medium text-cream transition-colors hover:bg-honey-dark"
                >
                  Take the Self-Care Quiz <ArrowRight size={16} />
                </button>
                <div className="mt-5 flex items-center justify-center gap-4">
                  <a href={BUSINESS.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="rounded-full border border-wood-900/15 p-2.5 text-wood-900">
                    <Instagram size={18} />
                  </a>
                  <a href={BUSINESS.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="rounded-full border border-wood-900/15 p-2.5 text-wood-900">
                    <Facebook size={18} />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
